import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'

import { CreateRes, PaginatedData, PaginationReq } from '@/apis/api.types'

import {
  changeSurveyStatus,
  createResponse,
  createSurvey,
  createTheme,
  deleteResponse,
  deleteSurvey,
  deleteTheme,
  duplicateSurvey,
  FetchSurveyReqDTO,
  getResponseDetails,
  getResponses,
  getSurveyDetails,
  getSurveyRespondents,
  getSurveys,
  getSurveyScoreDistribution,
  getSurveyTopPerformers,
  getThemes,
  RespondentListResDTO,
  RespondToSurveyReqDTO,
  ResponseDetailResDTO,
  ResponsesListResDTO,
  SubmissionStatus,
  SurveyDetail,
  SurveyListResDTO,
  SurveyScoreDistribution,
  Theme,
  TopPerformers,
  updateResponse,
  updateSurvey,
} from '@/apis/surveyApis'

export const surveyApiSlice = createApi({
  reducerPath: 'surveysApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Surveys', 'SurveyResponses', 'Theme'],

  endpoints: (builder) => ({
    getSurveys: builder.query<
      PaginatedData<SurveyListResDTO>,
      FetchSurveyReqDTO
    >({
      queryFn: async (data) => {
        try {
          return await getSurveys(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Surveys'],
    }),
    getSurveyDetails: builder.query<SurveyDetail, number>({
      queryFn: async (data) => {
        try {
          return await getSurveyDetails(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Surveys'],
    }),

    createSurvey: builder.mutation<{ data: SurveyListResDTO }, FormData>({
      queryFn: async (data) => {
        try {
          return await createSurvey(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Surveys'],
    }),
    updateSurvey: builder.mutation<{ data: SurveyDetail }, FormData>({
      queryFn: async (data) => {
        try {
          return await updateSurvey(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Surveys'],
    }),
    duplicateSurvey: builder.mutation<{ data: SurveyListResDTO }, number>({
      queryFn: async (data) => {
        try {
          return await duplicateSurvey(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Surveys'],
    }),
    changeSurveyStatus: builder.mutation<{ data: SurveyDetail }, number>({
      queryFn: async (data) => {
        try {
          return await changeSurveyStatus(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Surveys'],
    }),
    deleteSurvey: builder.mutation<{ data: any }, number>({
      queryFn: async (data) => {
        try {
          return await deleteSurvey(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Surveys'],
    }),

    getSurveyTopPerformers: builder.query<PaginatedData<TopPerformers>, number>(
      {
        queryFn: async (data) => {
          try {
            return await getSurveyTopPerformers(data)
          } catch (error) {
            throw new Error((error as any).response.data.message)
          }
        },
        providesTags: ['Surveys'],
      }
    ),
    getSurveyScoreDistribution: builder.query<SurveyScoreDistribution, number>({
      queryFn: async (data) => {
        try {
          return await getSurveyScoreDistribution(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Surveys'],
    }),
    getSurveyRespondents: builder.query<
      PaginatedData<RespondentListResDTO>,
      PaginationReq<{
        last_submission_status?: SubmissionStatus
        id: number
      }>
    >({
      queryFn: async (data) => {
        try {
          return await getSurveyRespondents(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Surveys'],
    }),
    getThemes: builder.query<PaginatedData<Theme>, PaginationReq>({
      queryFn: async (data) => {
        try {
          return await getThemes(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Theme'],
    }),
    createTheme: builder.mutation<{ data: CreateRes<Theme> }, Theme>({
      queryFn: async (data) => {
        try {
          return await createTheme(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Theme'],
    }),
    deleteTheme: builder.mutation<{ data: CreateRes<Theme> }, number>({
      queryFn: async (data) => {
        try {
          return await deleteTheme(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Theme'],
    }),

    getResponses: builder.query<
      PaginatedData<ResponsesListResDTO>,
      PaginationReq<{ survey?: number; respondent?: number }>
    >({
      queryFn: async (data) => {
        try {
          return await getResponses(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['SurveyResponses'],
    }),
    getResponseDetails: builder.query<ResponseDetailResDTO, number>({
      queryFn: async (data) => {
        try {
          return await getResponseDetails(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['SurveyResponses'],
    }),
    createResponse: builder.mutation<
      { data: ResponsesListResDTO },
      RespondToSurveyReqDTO & { surveyId: number }
    >({
      queryFn: async (data) => {
        try {
          return await createResponse(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['SurveyResponses', 'Surveys'],
    }),

    updateResponse: builder.mutation<
      { data: ResponsesListResDTO },
      RespondToSurveyReqDTO & { id: number }
    >({
      queryFn: async (data) => {
        try {
          return await updateResponse(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['SurveyResponses', 'Surveys'],
    }),
    deleteResponse: builder.mutation<{ data: ResponsesListResDTO }, number>({
      queryFn: async (data) => {
        try {
          return await deleteResponse(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['SurveyResponses', 'Surveys'],
    }),
  }),
})

export const {
  usePrefetch: usePrefetchSurveys,
  useGetSurveysQuery,
  useGetSurveyDetailsQuery,
  useDeleteSurveyMutation,
  useCreateSurveyMutation,
  useUpdateSurveyMutation,
  useDuplicateSurveyMutation,
  useChangeSurveyStatusMutation,
  useGetSurveyScoreDistributionQuery,
  useGetSurveyTopPerformersQuery,
  useGetSurveyRespondentsQuery,
  useGetThemesQuery,
  useDeleteThemeMutation,
  useCreateThemeMutation,
  useGetResponseDetailsQuery,
  useGetResponsesQuery,
  useCreateResponseMutation,
  useDeleteResponseMutation,
  useUpdateResponseMutation,
} = surveyApiSlice
export const initialiseSurveys = surveyApiSlice.endpoints.getSurveys.initiate
export const invalidateApi = surveyApiSlice.util.invalidateTags
